import { useApi } from "providers/UserProvider";
import styled from "styled-components";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from '@tiptap/extension-highlight'
import React, { useEffect, useRef } from "react";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import { EditorHeadingTray } from "./toolbars/EditorHeadingTray";
import { EditorHistoryTray } from "./toolbars/EditorHistoryTray";
import { EditorFormatTray } from "./toolbars/EditorFormatTray";
import { EditorColorsTray } from "./toolbars/EditorColorsTray";
import { getTableCellBackground, isCellMerged, isInTable } from "utilities/editorUtilities";



const EditorContext = React.createContext(null);

export const EditorInput = ({title, value = '', helptext, onChange, size = 'md'}) => {

    const { api } = useApi();

    const CustomImage = Image.extend({
        addAttributes() {
            return {
            ...this.parent?.(),
            'data-upload-id': {
                default: null,
            },
            }
    },
    });

    const editor = useEditor({
        extensions:
            [
                StarterKit,
                TextStyleKit,
                Highlight.configure({ multicolor: true }),
                CustomImage
            ],
        editorProps:
        {
            handlePaste: (_, event) => {
                const images = getImageFromClipboard(event);
                
                if(!images.length) return false;
                event.preventDefault();

                images.forEach(file => {
                    handleImagePaste(api,file, editor)
                });

                return true;
            }
        },
        onUpdate: ({editor}) => onChange(editor.getJSON()),
        content: value
    });

    const isInitialized = useRef(false);


    useEffect(() => {
        if (!editor || !value || isInitialized.current) return;

        editor.commands.setContent(value);
        isInitialized.current = true;
    }, [editor, value])

    

    const editorState = useEditorState({
        editor,
        selector: ({editor}) => ({
            canUndo: editor.can().chain().focus().undo().run(),
            canRedo: editor.can().chain().focus().redo().run(),

            headingLevel: editor.isActive('heading') ? 'h' + editor?.getAttributes('heading').level : 'paragraph',
            isBold: editor.isActive('bold'),
            isItalic: editor.isActive('italic'),
            isUnderline: editor.isActive('underline'),
            isStrike: editor.isActive('strike'),

            currentColor: editor.getAttributes('textStyle').color,
            currentHighlight: editor.getAttributes('highlight').color,

            currentAlignment:
                editor.isActive({ textAlign: 'right' }) ? 'right' :
                editor.isActive({ textAlign: 'center'}) ? 'center' :
                editor.isActive({ textAlign: 'justify'}) ? 'justify' : 'left',

            currentList:
                editor.isActive('bulletList') ? 'bulleted' :
                editor.isActive('orderedList') ? 'ordered' : 'none',

            isCellMerged: isCellMerged(editor),
            isInTable: isInTable(editor),
            currentCellBackground: getTableCellBackground(editor)
        }) 
    })

    return (
        <EditorContext.Provider value={{editor, editorState}}>
            <StyledEditorInput $size={size}>
                <span className="inputTitle">{title}</span>
                    <div className="editorContainer">
                        <div className="editortoolbar">
                            <EditorHistoryTray context={EditorContext}/>
                            <EditorHeadingTray context={EditorContext}/>
                            <EditorFormatTray context={EditorContext}/>
                            <EditorColorsTray context={EditorContext}/>
                        </div>
                        <div className="editorContent">
                            <EditorContent className="hiddenScrollbar" editor={editor}/>
                        </div>
                    </div>
                    {helptext ? <span className="inputHelptext">{helptext}</span> : null}
            </StyledEditorInput>
        </EditorContext.Provider>
    )
}

const StyledEditorInput = styled.div`

    display: flex;
    flex-direction: column;
    gap: ${({theme}) => theme.spacing.sm};
    
    span.inputTitle
    {
        font-size: ${({theme}) => theme.fontSize.md};
        font-weight: 500;
    }

    span.inputHelptext
    {
        font-size: ${({theme}) => theme.fontSize.sm};
        color: ${({theme}) => theme.colors.mutedForeground};
    }
    
    > div.editorContainer
    {
        display: flex;
        flex-direction: column;
        min-height: 100px;
        border: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        border-radius: ${({theme}) => theme.cornerRadius.md};
        background: ${({theme}) => theme.colors.neutralInputBackground};
        overflow: auto;
        transition-duration: 110ms;
        display: flex;

        &:hover
        {
            border: 1px solid ${({theme}) => theme.colors.mutedForeground};
        }

        &:has(.ProseMirror-focused)
        {
            border: 1px solid ${({theme}) => theme.colors.primaryForeground};

        }

        div.editortoolbar
        {
            padding: ${({theme}) => theme.spacing.sm} ${({theme}) => theme.spacing.lg};
            background: ${({theme}) => theme.colors.neutralSecondaryBackground};
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            //overflow: auto;
            gap: ${({theme}) => theme.spacing.sm};
            border-top-right-radius: ${({theme}) => theme.cornerRadius.md};
            border-top-left-radius: ${({theme}) => theme.cornerRadius.md};
            width: 100%;
            transition-duration: 110ms;
            

            > div.buttonsGroup
            {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: ${({theme}) => theme.spacing.sm};
            }
        }

        div.editorContent
        {
            height: ${({$size}) => $size === 'lg' ? '600px' : $size === 'md' ? '400px' : '200px'};
            padding: ${({theme}) => theme.spacing.md} ${({theme}) => theme.spacing.lg};
            display: flex;
            overflow: hidden;

            > div
            {
                width: 100%;
            }

            div.tiptap
            {
                width: 100%;
                height: 100%;
                flex: 1 1 auto;
                overflow: auto;
                outline: none;
                scrollbar-width: none !important;
                -ms-overflow-style: none !important;

                &::-webkit-scrollbar
                {
                    display: none !important;
                }

                ul, ol, li
                {
                    margin-left: 10px;
                }
            }
        }
    }
`

const getImageFromClipboard = (event) => {
    if (!event.clipboardData) return []

    const items = event.clipboardData.items
    const files = []

    for (const item of items)
    {
        if (item.type.startsWith('image/'))
        {
            const file = item.getAsFile()
            if(file)
            {
                files.push(file)
            }
        }
    }

  return files
}

const handleImagePaste = async (api, file, editor) => {
    if(!editor || !file) return;
    const uploadUuid = crypto.randomUUID()
    const objectUrl = URL.createObjectURL(file);

    editor
    .chain()
    .focus()
    .setImage({
        src: objectUrl,
        'data-upload-id': uploadUuid,
    })
    .run()
    
    try
    {
        const response = await api.uploadImage(file, uploadUuid);
        const imageUrl = response.url;
        
        editor.commands.command(({ tr, dispatch }) => {
            editor.state.doc.descendants((node, pos) => {
                if(node.type.name === 'image' && node.attrs['data-upload-id'] === uploadUuid)
                {
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        src: imageUrl,
                    });
                }
            })

            if (dispatch) dispatch(tr)
            return true
        })

        URL.revokeObjectURL(objectUrl)
    }
    catch(e)
    {
        console.error(e)
    }
}